import { BarController, CategoryScale, Chart as ChartJS, Legend, LineController, LineElement, LinearScale, PointElement, Title, Tooltip } from "chart.js";
import React, { useMemo } from "react";
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import moment from "moment";

ChartJS.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, BarController, Title, Tooltip, Legend);

const ChartBox = styled.div`
    width: 100%;
    height: 250px;
    // padding: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
`;

function EqmtAccuHourlyChart({ salesData, dateArray, searchData, crdInfo }) {
    const {t} = useTranslation();
    const now = moment();
    const today = now.clone().startOf('day').format("YYYY-MM-DD");
    const isToday = today === searchData.startDate;
    const currentHour = new Date().getHours();
    //const filteredSalesData = salesData.map(data => data.slice(0, currentHour + 1));
    
    const todayStacked = salesData[0];
    const yesterdayStacked = salesData[1];


    const chartData = useMemo(() => {
        if (!todayStacked || Object.keys(todayStacked).length === 0) {
            return { labels: [], datasets: [] };
        }
        // 결제수단별 고정 색상 (오늘/어제 구분 없이 같은 타입은 같은 색으로)
        const colorMap = {
            CARD: "#FF6384", CASH: "#3be219", CSEC: "#FFCE56", 
            CUPN: "#4BC0C0", EVCI: "#9966FF", FREE: "#FF9F40", 
            PONT: "#C9CBCF", QR: "#42A5F5"
        };

        // 1. 오늘 데이터 세트 생성
        const todayDatasets = Object.keys(todayStacked).map(payType => {
            const rawData = todayStacked[payType];
            const displayData = isToday ? rawData.slice(0, currentHour + 1) : rawData;
            return {
                type: "bar",
                label: `${payType} (${t("dashboardPage.today")})`, // 범례 구분용
                data: displayData,
                backgroundColor: colorMap[payType],
                stack: "today", // 오늘 막대 그룹
                barPercentage: 10, // 막대 너비 조절
                categoryPercentage: 0.5
            };
        });

        // 2. 어제 데이터 세트 생성
        const yesterdayDatasets = Object.keys(yesterdayStacked).map(payType => {
            return {
                type: "bar",
                label:`${searchData.division === "HST" ? `${payType} (${t("saleHistPage.yesterday")})` : `${payType} (${t("saleHistPage.lastWeek")})`}`,
                data: yesterdayStacked[payType],
                backgroundColor: colorMap[payType] + "50", // 어제 데이터는 약간 투명하게(80 추가) 구분
                borderColor: colorMap[payType],
                borderWidth: 1,
                stack: "yesterday", // 어제 막대 그룹
                barPercentage: 10,
                categoryPercentage: 0.5
            };
        });

        return {
            labels: dateArray.map(data => `${data}${t("dashboardPage.hour")}`),
            datasets: [...todayDatasets, ...yesterdayDatasets] // 두 리스트를 합침
        };
            // return {
            //     labels: dateArray.map(data => `${data}${t("dashboardPage.hour")}`),
            //     datasets: [
            //         {
            //             type: "bar",
            //             label: t("saleHistPage.todayAccuSales"),
            //             data: isToday ? filteredSalesData[0] : salesData[0],
            //             backgroundColor: "#FF7F00",
            //         },
            //         {
            //             type: "bar",
            //             label: `${searchData.division === "HST" ? t("saleHistPage.yesterdayAccuSales") : t("saleHistPage.lastWeekAccuSales")}`,
            //             data: salesData[1],
            //             backgroundColor: "#006DB2",
            //         }
            //     ],
            // };
    //}, [filteredSalesData, salesData, dateArray, t, isToday, searchData]);
    }, [todayStacked, yesterdayStacked, dateArray, t, isToday, currentHour]);

    const options = {
        responsive: true,
        maintainAspectRatio: false, //부모의 크기에 맞춤
        scales: {
            x: {
                stacked: false,
                ticks: {
                    font: {
                        size: 10
                    },
                    minRotation: 90,
                    padding: 5
                }
            },
            y: {
                stacked: true, // Y축은 반드시 true여야 결제수단별로 쌓임
                beginAtZero: true,
                ticks: {
                    callback: (value) => value.toLocaleString() // 천단위 콤마
                }
            },
        },
        plugins: {
            legend: {
                display: true, // 범례
                position: 'bottom',
                labels: {
                    // 범례 필터링: 해당 데이터셋의 모든 값이 0이면 범례에서 제외
                    filter: function(item, chart) {
                        // 1. 차트 데이터셋이 존재하는지 아주 엄격하게 체크
                        const datasets = chart?.data?.datasets;
                        if (!datasets || !datasets[item.datasetIndex]) {
                            return false;
                        }
                    
                        const dataset = datasets[item.datasetIndex];
                        
                        // 2. 데이터 배열이 있고, 그 중 0보다 큰 숫자가 하나라도 있는지 확인
                        // (null이나 undefined가 섞여 있어도 에러 나지 않게 방어)
                        if (dataset && Array.isArray(dataset.data)) {
                            return dataset.data.some(value => value !== null && value !== undefined && value > 0);
                        }
                    
                        return false;
                    },
                    font: {
                        size: 11,
                    },
                    boxWidth: 6,
                    boxHeight: 6
                }
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        label += `${crdInfo && crdInfo.natnCd === "KR" ? "₩" : crdInfo && crdInfo.natnCd === "JP" ? "¥" : "$"} ${context.raw.toLocaleString()}`;
                        return label;
                    }
                }
            }
        },
    };

    return (
        <ChartBox>
            <Bar options={options} data={chartData} />
        </ChartBox>
    )

}
export default EqmtAccuHourlyChart;
